import CodiceFiscale from "codice-fiscale-js";

type CodiceFiscaleNameCodeApi = typeof CodiceFiscale & {
    surnameCode(value: string): string;
    nameCode(value: string): string;
    computeInverse(value: string): {
        day: number;
        month: number;
        year: number;
        gender: string;
    };
    findLocationCode(name: string, province?: string): string;
};

const codiceFiscaleNameCodeApi = CodiceFiscale as CodiceFiscaleNameCodeApi;

export type RegistrationAction =
    | "REGISTER"
    | "ACCOUNT_EXISTS"
    | "RESUME_VERIFICATION";

const fiscalCodePattern = /^[A-Z]{6}[0-9]{2}[ABCDEHLMPRST][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

export const normalizeFiscalCode = (value: string) => value.replace(/\s+/g, "").toUpperCase();

export const validateFiscalCode = (value: string) => {
    const fiscalCode = normalizeFiscalCode(value);

    if (!fiscalCodePattern.test(fiscalCode)) {
        return false;
    }

    try {
        return CodiceFiscale.check(fiscalCode);
    } catch {
        return false;
    }
};

export const normalizeEmail = (value: string) => value.trim().toLowerCase();

const normalizePersonName = (value: string) => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();

export const validateFiscalCodeIdentity = (name: string, surname: string, value: string) => {
    const fiscalCode = normalizeFiscalCode(value);
    const normalizedName = normalizePersonName(name);
    const normalizedSurname = normalizePersonName(surname);

    if (!validateFiscalCode(fiscalCode) || !normalizedName || !normalizedSurname) {
        return false;
    }

    try {
        return codiceFiscaleNameCodeApi.surnameCode(normalizedSurname) === fiscalCode.slice(0, 3)
            && codiceFiscaleNameCodeApi.nameCode(normalizedName) === fiscalCode.slice(3, 6);
    } catch {
        return false;
    }
};

export type FiscalCodePersonalDataIssue =
    | "fiscalCode"
    | "identity"
    | "birthDate"
    | "gender"
    | "birthplace"
    | "birthplaceUnavailable";

export const validateFiscalCodePersonalData = (data: {
    name?: string;
    surname?: string;
    birthplace?: string;
    birthplaceProvince?: string;
    birthDate?: Date;
    gender?: string;
    fiscalCode: string;
}): FiscalCodePersonalDataIssue[] => {
    const fiscalCode = normalizeFiscalCode(data.fiscalCode);

    if (!validateFiscalCode(fiscalCode)) {
        return ["fiscalCode"];
    }

    const issues: FiscalCodePersonalDataIssue[] = [];

    if (data.name && data.surname && !validateFiscalCodeIdentity(data.name, data.surname, fiscalCode)) {
        issues.push("identity");
    }

    try {
        const decoded = codiceFiscaleNameCodeApi.computeInverse(fiscalCode);

        if (data.birthDate
            && (data.birthDate.getFullYear() !== decoded.year
                || data.birthDate.getMonth() + 1 !== decoded.month
                || data.birthDate.getDate() !== decoded.day)) {
            issues.push("birthDate");
        }

        if (data.gender) {
            const expectedGender = data.gender.toLowerCase() === "femmina" ? "F" : "M";
            if (decoded.gender !== expectedGender) {
                issues.push("gender");
            }
        }

        if (data.birthplace && data.birthplaceProvince) {
            try {
                const expectedLocationCode = codiceFiscaleNameCodeApi.findLocationCode(
                    data.birthplace,
                    data.birthplaceProvince,
                );

                if (expectedLocationCode !== fiscalCode.slice(11, 15)) {
                    issues.push("birthplace");
                }
            } catch {
                issues.push("birthplace", "birthplaceUnavailable");
            }
        }
    } catch {
        issues.push("fiscalCode");
    }

    return issues;
};

export const classifyRegistrationConflict = (payload: {
    code?: string;
    nextAction?: string;
}): Exclude<RegistrationAction, "REGISTER"> | null => {
    if (payload.code === "ACCOUNT_ALREADY_EXISTS" || payload.nextAction === "ACCOUNT_EXISTS") {
        return "ACCOUNT_EXISTS";
    }

    if (payload.nextAction === "RESUME_VERIFICATION") {
        return "RESUME_VERIFICATION";
    }

    return null;
};

export const getRegistrationConflictMessage = (payload: {
    code?: string;
}): string => payload.code === "EMAIL_ALREADY_EXISTS"
    ? "Esiste già un account associato a questa email."
    : "Esiste già un account associato a questo codice fiscale.";
